import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  IconButton,
  Switch,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button, Tooltip,
  Box,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import { CopyIcon } from 'Shared/Icons';
import { AlertDialog } from 'Shared/AlertDialog';
import FlowsGraph from 'Shared/flows/FlowsGraph';
import FlowPerformanceGraph from 'Shared/flows/FlowPerformanceGraph';
import FlowCopyForm from 'Shared/flows/flows-interface/FlowCopyForm';
import { setDragData } from 'Shared/flows/folders/folderUtils';
import FlowFolderLabel from 'Shared/flows/folders/FlowFolderLabel';
import FlowIdLabel from 'Shared/flows/FlowIdLabel';
import FlowRunButton from 'Shared/flows/FlowRunButton';

export const AdminFlowsItem = ({ item, needUpdate, deleteFlow, changeReadonlyFlow, userRole, onOpenCompanyAccessModal, selected, onToggleSelect, folderPath }) => {
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isActiveModal, setIsActiveModal] = useState(false);
  const [visibleCopyFlowForm, setVisibleCopyFlowForm] = useState(false);
  const [confirmButtonText, setConfirmButtonText] = useState('Yes');
  const [skipButtonText, setSkipButtonText] = useState('No');
  const [modalAction, setModalAction] = useState('change');
  const [modalTitle, setModalTitle] = useState('Confirm your action');
  const [modalText, setModalText] = useState(
      <span>Change <strong>{item.name}</strong> availability?</span>
  );

  const toFlow = (event, id) => {
    event.preventDefault();
    navigate(`/flow/${id}`);
  };

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded((prev) => (prev === 'panel1' ? false : 'panel1'));
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    setVisibleCopyFlowForm(true);
  };

  const handleChangeSwitch = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Admin: открыть modal управления доступом компаний
    if (userRole === 'Admin' && onOpenCompanyAccessModal) {
      onOpenCompanyAccessModal(item.id);
    }
  };

  const handleChangeReadonlySwitch = (e) => {
    e.preventDefault();
    setModalTitle('Confirm your action');
    setModalText(<span>Change <strong>{item.name}</strong> readonly status?</span>);
    setConfirmButtonText('Yes');
    setSkipButtonText('No');
    setModalAction('readonly');
    setIsActiveModal(true);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setModalTitle(`Delete "${item.name}"?`);
    setModalText(<span>Are you sure you want to delete <strong>{item.name}</strong>?</span>);
    setConfirmButtonText('Delete');
    setSkipButtonText('Cancel');
    setModalAction('delete');
    setIsActiveModal(true);
  };

  const onClose = () => setIsActiveModal(false);

  const onAgree = () => {
    setIsActiveModal(false);
    if (modalAction === 'delete') {
      deleteFlow(item);
    } else if (modalAction === 'readonly') {
      changeReadonlyFlow(item);
    }
  };

  const skipButton = modalAction === 'delete' ? (
      <Button
          sx={{ backgroundColor: '#e9e9e9', color: '#707070', borderRadius: '5px' }}
          onClick={onClose}
      >
        {skipButtonText}
      </Button>
  ) : null;

  const confirmButton = modalAction === 'delete' ? (
      <Button
          sx={{
            backgroundColor: '#f44336',
            color: '#fff',
            borderRadius: '5px',
            '&:hover': { backgroundColor: '#bf2317' },
          }}
          onClick={onAgree}
      >
        {confirmButtonText}
      </Button>
  ) : null;

  return (
      <>
        <AlertDialog
            visible={isActiveModal}
            onClose={onClose}
            onAgree={onAgree}
            text={modalText}
            title={modalTitle}
            noButton={skipButton}
            yesButton={confirmButton}
            yesButtonText={confirmButtonText}
            noButtonText={skipButtonText}
        />
        <FlowCopyForm flowName={item.name}
            id={item.id}
            needUpdateList={needUpdate}
            setVisibleFlowForm={setVisibleCopyFlowForm}
            visibleFlowForm={visibleCopyFlowForm}
        />
        <Accordion
            sx={{ marginBottom: '20px' }}
            expanded={expanded === 'panel1'}
        >
          <AccordionSummary
              draggable
              onDragStart={(e) => setDragData(e, { kind: 'flow', id: item.id })}
              sx={{
                backgroundColor: '#F2F5F8',
                '& .MuiAccordionSummary-content': {alignItems: 'center'},
              }}
              expandIcon={
              <Tooltip title={
                <span style={{fontSize: '1rem', lineHeight: '120%'}}>Edit Flow</span>
              } arrow>
                <IconButton onClick={(e) => toFlow(e, item.id)}>
                  <SettingsIcon/>
                </IconButton>
              </Tooltip>
              }
              aria-controls="panel1bh-content"
              id="panel1bh-header">
            {onToggleSelect && (
              <Checkbox
                checked={selected}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSelect(item)}
                sx={{ marginRight: '8px', flexShrink: 0 }}
              />
            )}
            <FlowIdLabel id={item.id} externalId={item.external_id} />
            <Box
                sx={{ flex: 1, minWidth: 0, marginRight: '20px', cursor: 'pointer' }}
                onClick={(e) => toFlow(e, item.id)}
            >
              <FlowFolderLabel path={folderPath} />
              <Typography
                  variant="h6"
                  sx={{ color: '#3B465C', overflowWrap: 'anywhere' }}
              >
                {item?.name}
                {item?.company_name && (
                  <span style={{ fontSize: '0.85rem', color: '#888', fontWeight: 400, marginLeft: 6 }}>
                    ({item.company_name})
                  </span>
                )}
              </Typography>
              {item?.description && item.description !== item.name && (
                <Typography
                    variant="body2"
                    sx={{ color: '#888', fontSize: '0.8rem', overflowWrap: 'anywhere' }}
                >
                  {item.description}
                </Typography>
              )}
            </Box>

            <Tooltip title={
              <span style={{fontSize: '1rem', lineHeight: '120%'}}>Make the flow visible to company users</span>
            } arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontSize: '0.75rem',
                    marginRight: '8px',
                    fontWeight: 500
                  }}
                >
                  Access
                </Typography>
                <Switch
                    checked={item.is_active_for_any_company || false}
                    color="primary"
                    onClick={handleChangeSwitch}
                />
              </Box>
            </Tooltip>

            {userRole === 'Admin' && (
              <Tooltip title={
                                    <span style={{fontSize: '1rem', lineHeight: '120%'}}>Prevent any company user except the flow owner from editing the flow</span>
              } arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '16px', flexShrink: 0 }}>
                                 <Typography
                 variant="body2"
                 sx={{
                   color: '#666',
                   fontSize: '0.75rem',
                   marginRight: '8px',
                   fontWeight: 500
                 }}
                                >
                   Read only
                 </Typography>
                  <Switch
                      checked={item.readonly === 1 || item.readonly === '1' || item.readonly === true}
                      color="primary"
                      onClick={handleChangeReadonlySwitch}
                  />
                </Box>
              </Tooltip>
            )}

            <Tooltip title={
              <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Usage Statistics</span>
            } arrow>
              <IconButton
                  sx={{ marginRight: '14px', color: expanded ? '#F4B156' : '' }}
                  aria-label="open-chart"
                  onClick={toggleExpanded}
              >
                <StackedBarChartIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={
              <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Copy Flow</span>
            } arrow>
              <IconButton
                  sx={{ marginRight: '14px', color: '#f44336' }}
                  onClick={handleCopy}
                  aria-label="copy"
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={
              <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Delete Flow</span>
            } arrow>
              <IconButton
                  sx={{ marginRight: '14px', color: '#f44336' }}
                  onClick={handleDelete}
                  aria-label="delete"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <FlowRunButton flow={item} />
          </AccordionSummary>
          <AccordionDetails>
            {expanded ? (
              <Box>
                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}
                >
                  <Tab label="API Requests" />
                  <Tab label="Performance" />
                </Tabs>
                {selectedTab === 0 && <FlowsGraph item={item}/>}
                {selectedTab === 1 && <FlowPerformanceGraph item={item}/>}
              </Box>
            ) : null}
          </AccordionDetails>
        </Accordion>
      </>
  );
};
